import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
    Mail,
    Phone,
    MapPin,
    ChevronRight,
    Building2,
    Map,
    Scale,
    MessageSquareMore
} from "lucide-react";
import footerData from "./Footer.json";
import "./Footer.css";
import { cdnUrl } from "../../config/cdn";

// icon mapping
const ICONS = {
    MapPin: MapPin,
    Mail: Mail,
    Phone: Phone,
    Building2: Building2,
    Map: Map,
    Scale: Scale,
};

const Footer = () => {
    const [showCertificates, setShowCertificates] = useState(false);
    const [stickToFooter, setStickToFooter] = useState(false);
    const {
        bgColor,
        textColor,
        accentColor,
        logo,
        contact,
        navigation,
        certIcons,
        socials,
        bottomText,
    } = footerData;

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.2, delayChildren: 0.15 },
        },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 40 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
        },
    };
    const certSlotRef = useRef(null);

    useEffect(() => {
        const handleCertPosition = () => {
            const shouldShow = window.scrollY > 300;
            setShowCertificates(shouldShow);

            if (!certSlotRef.current || !shouldShow) {
                setStickToFooter(false);
                return;
            }

            const slotRect = certSlotRef.current.getBoundingClientRect();
            const floatingBottom = 30;
            const certificateHeight = 55;
            const floatingTop = window.innerHeight - floatingBottom - certificateHeight;

            setStickToFooter(slotRect.top <= floatingTop);
        };

        handleCertPosition();
        window.addEventListener("scroll", handleCertPosition, { passive: true });
        window.addEventListener("resize", handleCertPosition);

        return () => {
            window.removeEventListener("scroll", handleCertPosition);
            window.removeEventListener("resize", handleCertPosition);
        };
    }, []);

    return (
        <footer
    className="footer-modern"
    style={{ backgroundColor: bgColor, color: textColor }}
        >
            <div className="footer-container">
                <motion.div
                    className="footer-grid"
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                >
                    {/* Left section */}
                    <motion.div className="footer-section" variants={itemVariants}>
                        <h3 className="footer-nav-title">
                            <MessageSquareMore
                                style={{
                                    width: 22,
                                    height: 22,
                                    marginRight: 10,
                                    color: accentColor,
                                }}
                            />
                            {contact.title}
                        </h3>
                        {contact.items.map((item, i) => {
                            const Icon = ICONS[item.icon];
                            return (
                                <ContactItem
                                    key={i}
                                    icon={Icon ? <Icon /> : null}
                                    label={item.label}
                                    text={item.text}
                                    href={item.href}
                                    accent={accentColor}
                                />
                            );
                        })}

                        <motion.div whileHover={{ scale: 1.03 }}>
                            <a href="/Home">
                                <div
                                    className="footer-logo-card"
                                    style={{ borderColor: accentColor }}
                                >
                                    <img
                                        src={cdnUrl(logo)}
                                        alt="Logo"
                                        className="footer-logo-img"
                                    />
                                </div>
                            </a>
                        </motion.div>
                    </motion.div>

                    {/* Navigation */}
                    <motion.div
                        className="footer-nav-area"
                        variants={itemVariants}
                    >
                        {navigation.map((nav, idx) => {
                            const Icon = ICONS[nav.icon];
                            return (
                                <NavigationColumn
                                    key={idx}
                                    title={nav.title}
                                    links={nav.links}
                                    accent={accentColor}
                                    icon={Icon}
                                />
                            );
                        })}
                    </motion.div>
                </motion.div>

                {/* Certificates */}
                <div ref={certSlotRef} className="footer-cert-footer">
                    {showCertificates && stickToFooter && certIcons.map((icon, i) => (
                        <img key={i} src={cdnUrl(icon)} alt="cert" />
                    ))}
                </div>

                {showCertificates && !stickToFooter && (
                    <div className="footer-cert-floating">
                        {certIcons.map((icon, i) => (
                            <img key={i} src={cdnUrl(icon)} alt="cert" />
                        ))}
                    </div>
                )}

                {/* Bottom */}
                <div className="footer-bottom">
                    <p>{bottomText}</p>

                    <div className="footer-socials">
                        {socials.map((s, i) => (
                            <a
                                key={i}
                                href={s.url}
                                target="_blank"
                                rel="noreferrer"
                                className="footer-social-icon"
                            >
                                <img src={cdnUrl(s.icon)} alt="social" />
                            </a>
                        ))}
                    </div>
                </div>
            </div>
        </footer>
    );
};

// --------------------
// Helper Components
// --------------------

const ContactItem = ({ icon, label, text, href, accent }) => {
    const content = (
        <div className="footer-contact-item">
            <div
                className="footer-contact-icon"
                style={{ color: accent }}
            >
                {icon}
            </div>
            <div>
                <p
                    className="footer-contact-label"
                    style={{ color: accent }}
                >
                    {label}
                </p>
                <p className="footer-contact-text">{text}</p>
            </div>
        </div>
    );

    return href ? <a href={href}>{content}</a> : content;
};

const NavigationColumn = ({ title, links, accent, icon: Icon }) => (
    <motion.div className="footer-nav-column">
        <h3 className="footer-nav-title">
            {Icon && (
                <Icon
                    style={{
                        width: 22,
                        height: 22,
                        marginRight: 10,
                        color: accent,
                    }}
                />
            )}
            {title}
        </h3>

        <div className="footer-nav-links">
            {links.map((link, i) => (
                <motion.a
                    key={i}
                    href={link.url}
                    className="footer-nav-link"
                >
                    <ChevronRight
                        className="footer-nav-icon"
                        stroke={accent}
                    />
                    {link.name}
                </motion.a>
            ))}
        </div>
    </motion.div>
);

export default Footer;
